import { reactive, ref, computed, nextTick } from 'vue'
import { trackEvent } from '~/composables/analytics'
import { response } from '~/assets/js/colorData.js'

// Everything about selecting / filtering / deleting colors while editing a
// palette. `onColorCopied` lets the page surface its "Color Copied!" toast
// without this composable owning notification state.
export function usePaletteSelection({ onColorCopied }: { onColorCopied?: () => void } = {}) {
  const enableEdit = ref(false)
  const showFilter = ref(false)

  const selectedColors = reactive({
    visualPalette: {
      'Brand Colors': [] as any[],
      'Logo Colors': [] as any[],
      'Homepage Colors': [] as any[],
      'Custom Colors': [] as any[],
    },
    nearestColors: {} as Record<string, any[]>,
  })

  const editToggle = () => {
    enableEdit.value = !enableEdit.value
    try {
      trackEvent('Edit Toggle')
    } catch {}
  }

  const isVisualCategory = (category: string) =>
    category === 'Brand Colors' ||
    category === 'Logo Colors' ||
    category === 'Homepage Colors' ||
    category === 'Custom Colors'

  const toggleSelection = (category: string, colorObj: any, index: number) => {
    if (enableEdit.value === true) {
      showFilter.value = true
      let targetCategory

      if (isVisualCategory(category)) {
        targetCategory = selectedColors.visualPalette[category]
      } else {
        if (!selectedColors.nearestColors[category]) {
          selectedColors.nearestColors[category] = []
        }
        targetCategory = selectedColors.nearestColors[category]
      }

      const existingIndex = targetCategory.findIndex(
        (selected) => selected.color === colorObj.color && selected.index === index,
      )

      if (existingIndex === -1) {
        targetCategory.push({ ...colorObj, index })
      } else {
        targetCategory.splice(existingIndex, 1)
      }

      // Clean up empty categories in nearestColors
      if (!isVisualCategory(category)) {
        if (targetCategory.length === 0) {
          delete selectedColors.nearestColors[category]
        }
      }
    }
  }

  const checkVisualPaletteSelection = (category: string, colorObj: any, index: number) => {
    const selectedArray = selectedColors.visualPalette[category]
    if (!selectedArray || selectedArray.length === 0) return false

    return selectedArray.some(
      (selected) =>
        selected.color === colorObj.color && selected.name === colorObj.name && selected.index === index,
    )
  }

  const checkNearestColorsSelection = (category: string, colorObj: any, index: number) => {
    const selectedArray = selectedColors.nearestColors[category]
    if (!selectedArray || selectedArray.length === 0) return false

    return selectedArray.some(
      (selected) =>
        selected.color === colorObj.color &&
        selected.name === colorObj.name &&
        selected.group === colorObj.group &&
        selected.index === index,
    )
  }

  const isColorSelected = (category: string, colorObj: any, index: number) => {
    if (isVisualCategory(category)) {
      return checkVisualPaletteSelection(category, colorObj, index)
    }
    return checkNearestColorsSelection(category, colorObj, index)
  }

  function mergePalettes() {
    try {
      response.visualPalette['Brand Colors'] = []
      response.visualPalette['Logo Colors'] = []
      response.visualPalette['Homepage Colors'] = []
      response.visualPalette['Custom Colors'] = []
      response.nearestColors = {}
      Object.assign(response.nearestColors, selectedColors.nearestColors)
      Object.assign(response.visualPalette, selectedColors.visualPalette)
    } finally {
      resetFiltering()
    }
  }

  function resetFiltering() {
    showFilter.value = false
    selectedColors.visualPalette['Brand Colors'] = []
    selectedColors.visualPalette['Logo Colors'] = []
    selectedColors.visualPalette['Homepage Colors'] = []
    selectedColors.visualPalette['Custom Colors'] = []
    selectedColors.nearestColors = {}
  }

  function copyToClipboard(color: string) {
    try {
      trackEvent('Color Copied')
    } catch {}
    navigator.clipboard
      .writeText(color)
      .then(() => {
        if (onColorCopied) onColorCopied()
      })
      .catch(() => {})
  }

  const deleteColorVisual = (index: number, paletteType: string) => {
    const colorToDelete = response.visualPalette[paletteType][index]

    // Remove from response
    response.visualPalette[paletteType].splice(index, 1)

    // Remove from selectedColors and update indices
    if (selectedColors.visualPalette[paletteType]) {
      const selectedIndex = selectedColors.visualPalette[paletteType].findIndex(
        (color) =>
          color.color === colorToDelete.color && color.name === colorToDelete.name && color.index === index,
      )
      if (selectedIndex !== -1) {
        selectedColors.visualPalette[paletteType].splice(selectedIndex, 1)
      }

      // Update indices of remaining selected colors
      selectedColors.visualPalette[paletteType].forEach((color) => {
        if (color.index > index) {
          color.index--
        }
      })
    }

    // Trigger reactivity
    nextTick(() => {
      response.visualPalette[paletteType] = [...response.visualPalette[paletteType]]
      selectedColors.visualPalette = { ...selectedColors.visualPalette }
    })
  }

  const deleteColorNearest = (group: string, index: number) => {
    const colorToDelete = response.nearestColors[group][index]

    // Remove from response
    response.nearestColors[group].splice(index, 1)

    if (response.nearestColors[group].length === 0) {
      delete response.nearestColors[group]
    }

    // Remove from selectedColors
    if (selectedColors.nearestColors[group]) {
      const selectedIndex = selectedColors.nearestColors[group].findIndex(
        (color) => color.color === colorToDelete.color && color.name === colorToDelete.name,
      )
      if (selectedIndex !== -1) {
        selectedColors.nearestColors[group].splice(selectedIndex, 1)
      }
      if (selectedColors.nearestColors[group].length === 0) {
        delete selectedColors.nearestColors[group]
      } else {
        // Update indices of remaining selected colors
        selectedColors.nearestColors[group].forEach((color) => {
          if (color.index > index) {
            color.index--
          }
        })
      }
    }

    // Trigger reactivity
    nextTick(() => {
      response.nearestColors = { ...response.nearestColors }
      selectedColors.nearestColors = { ...selectedColors.nearestColors }
    })
  }

  const totalVisualSelectedColors = computed(() => {
    if (!selectedColors.visualPalette || typeof response.visualPalette !== 'object') {
      return 0
    }
    return Object.values(selectedColors.visualPalette).reduce((acc, colors) => {
      if (Array.isArray(colors)) {
        return acc + colors.length
      }
      return acc
    }, 0)
  })

  const totalCodeSelectedColors = computed(() => {
    if (!selectedColors.nearestColors || typeof selectedColors.nearestColors !== 'object') {
      return 0
    }
    return Object.values(selectedColors.nearestColors).reduce((acc, colors) => {
      if (Array.isArray(colors)) {
        return acc + colors.length
      }
      return acc
    }, 0)
  })

  return {
    enableEdit,
    showFilter,
    selectedColors,
    editToggle,
    toggleSelection,
    isColorSelected,
    mergePalettes,
    resetFiltering,
    copyToClipboard,
    deleteColorVisual,
    deleteColorNearest,
    totalVisualSelectedColors,
    totalCodeSelectedColors,
  }
}
